import { useCallback, useEffect, useRef, useState } from "react";
import {
  formatPreviewTime,
  getPreviewScrollMetrics,
  getScrollRate,
} from "./previewPlaybackUtils";

const PLAYBAR_HIDE_MS = 5000;

/**
 * Scroll live-preview playback: rAF loop, seek/time, speed, and playbar chrome.
 *
 * scrollScale: the display zoom applied to the credits (display width ÷
 * canonical layout width, in editor and fullscreen). Zoom multiplies
 * scrollHeight, so the px/sec rate is multiplied by the same factor —
 * running time stays canonical and identical on every screen.
 */
export default function usePreviewPlayback({
  previewRef,
  ppf,
  playbackFps,
  isRenderPage,
  creditsLength,
  scrollScale = 1,
  layoutVersion,
}) {
  const effectiveScale = scrollScale > 0 ? scrollScale : 1;

  const isPlayingRef = useRef(false);
  const isSeekingRef = useRef(false);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const scrollAccumRef = useRef(0);
  const isPlaybarHoveredRef = useRef(false);
  const isPreviewHoveredRef = useRef(false);
  const playbarHideTimerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentSec, setCurrentSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [isPlaybarVisible, setIsPlaybarVisible] = useState(true);
  const [isAtLastBlock, setIsAtLastBlock] = useState(false);

  const clearPlaybarHideTimer = useCallback(() => {
    if (playbarHideTimerRef.current) {
      clearTimeout(playbarHideTimerRef.current);
      playbarHideTimerRef.current = null;
    }
  }, []);

  const schedulePlaybarHide = useCallback(() => {
    clearPlaybarHideTimer();
    playbarHideTimerRef.current = setTimeout(() => {
      if (!isPlayingRef.current) return;
      isPlaybarHoveredRef.current = false;
      setIsPlaybarVisible(false);
    }, PLAYBAR_HIDE_MS);
  }, [clearPlaybarHideTimer]);

  const revealPlaybar = useCallback(() => {
    setIsPlaybarVisible(true);
    if (isPlayingRef.current) {
      schedulePlaybarHide();
    } else {
      clearPlaybarHideTimer();
    }
  }, [schedulePlaybarHide, clearPlaybarHideTimer]);

  const hidePlaybar = useCallback(() => {
    if (!isPlayingRef.current) {
      clearPlaybarHideTimer();
      setIsPlaybarVisible(true);
      return;
    }
    isPlaybarHoveredRef.current = false;
    clearPlaybarHideTimer();
    setIsPlaybarVisible(false);
  }, [clearPlaybarHideTimer]);

  const syncProgressFromScroll = useCallback(() => {
    const el = previewRef.current;
    const { scrollTop, maxScroll } = getPreviewScrollMetrics(el);
    const rate = getScrollRate(ppf, playbackFps) * effectiveScale;
    setProgress(maxScroll > 0 ? scrollTop / maxScroll : 0);
    setCurrentSec(scrollTop / rate);
    setDurationSec(maxScroll / rate);

    // Compact once the trailing blank (or last credit) is the main on-screen
    // block, not when an earlier block is centered and the end merely peeks.
    const wrappers = el?.querySelectorAll(".blockWrapper");
    const last =
      el?.querySelector(".previewBlankScreen--end") ||
      wrappers?.[wrappers.length - 1];
    if (!el || !last) {
      setIsAtLastBlock(false);
      return;
    }
    const frame = el.getBoundingClientRect();
    const box = last.getBoundingClientRect();
    const mainLine = frame.top + frame.height * 0.55;
    setIsAtLastBlock(box.top < mainLine && box.bottom > frame.top + 8);
  }, [previewRef, ppf, playbackFps, effectiveScale]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Auto-hide playbar while hovering the preview container during playback.
  useEffect(() => {
    const host = document.getElementById("preview-container");
    if (!host) return undefined;

    const onEnterOrMove = () => {
      isPreviewHoveredRef.current = true;
      revealPlaybar();
    };
    const onLeave = () => {
      isPreviewHoveredRef.current = false;
      hidePlaybar();
    };

    host.addEventListener("mouseenter", onEnterOrMove);
    host.addEventListener("mousemove", onEnterOrMove);
    host.addEventListener("mouseleave", onLeave);

    if (host.matches(":hover")) {
      isPreviewHoveredRef.current = true;
      revealPlaybar();
    }

    return () => {
      host.removeEventListener("mouseenter", onEnterOrMove);
      host.removeEventListener("mousemove", onEnterOrMove);
      host.removeEventListener("mouseleave", onLeave);
      clearPlaybarHideTimer();
    };
  }, [revealPlaybar, hidePlaybar, clearPlaybarHideTimer]);

  useEffect(() => {
    if (isPlaying) {
      revealPlaybar();
      return undefined;
    }
    clearPlaybarHideTimer();
    setIsPlaybarVisible(true);
    return undefined;
  }, [isPlaying, revealPlaybar, clearPlaybarHideTimer]);

  useEffect(() => {
    if (isRenderPage && isPlaying) {
      setIsPlaying(false);
    }
  }, [isRenderPage, isPlaying]);

  // Keep seek/time in sync with manual scroll and content size changes.
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;

    const onScroll = () => {
      if (isSeekingRef.current) return;
      syncProgressFromScroll();
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    syncProgressFromScroll();

    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => syncProgressFromScroll());
      ro.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro?.disconnect();
    };
  }, [previewRef, syncProgressFromScroll, creditsLength, layoutVersion]);

  // rAF loop — accumulate fractional pixels so low ppf (e.g. 1) still advances.
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;

    if (!isPlaying) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      el.style.scrollBehavior = "";
      lastTsRef.current = 0;
      scrollAccumRef.current = 0;
      return;
    }

    el.style.scrollBehavior = "auto";
    lastTsRef.current = 0;
    scrollAccumRef.current = 0;

    const tick = (ts) => {
      if (!isPlayingRef.current || isSeekingRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (!lastTsRef.current) {
        lastTsRef.current = ts;
      }
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      const { maxScroll } = getPreviewScrollMetrics(el);
      if (maxScroll <= 0) {
        setIsPlaying(false);
        return;
      }

      const rate = getScrollRate(ppf, playbackFps) * effectiveScale;
      scrollAccumRef.current += rate * dt;
      const step = Math.floor(scrollAccumRef.current);
      if (step < 1) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      scrollAccumRef.current -= step;

      const nextTop = el.scrollTop + step;

      if (nextTop >= maxScroll) {
        el.scrollTop = maxScroll;
        scrollAccumRef.current = 0;
        syncProgressFromScroll();
        setIsPlaying(false);
        return;
      }

      el.scrollTop = nextTop;
      syncProgressFromScroll();
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      el.style.scrollBehavior = "";
    };
  }, [isPlaying, ppf, playbackFps, effectiveScale, previewRef, syncProgressFromScroll]);

  const handlePlayPause = useCallback(() => {
    const el = previewRef.current;
    if (!el) return;
    const { scrollTop, maxScroll } = getPreviewScrollMetrics(el);
    if (!isPlaying && maxScroll > 0 && scrollTop >= maxScroll - 1) {
      el.style.scrollBehavior = "auto";
      el.scrollTop = 0;
      scrollAccumRef.current = 0;
      syncProgressFromScroll();
    }
    setIsPlaying((prev) => !prev);
  }, [isPlaying, previewRef, syncProgressFromScroll]);

  const handleRestart = useCallback(() => {
    const el = previewRef.current;
    if (!el) return;
    el.style.scrollBehavior = "auto";
    el.scrollTop = 0;
    scrollAccumRef.current = 0;
    syncProgressFromScroll();
    lastTsRef.current = 0;
  }, [previewRef, syncProgressFromScroll]);

  const handleSeekStart = useCallback(() => {
    isSeekingRef.current = true;
  }, []);

  const handleSeek = useCallback(
    (value) => {
      const el = previewRef.current;
      if (!el) return;
      const { maxScroll } = getPreviewScrollMetrics(el);
      el.style.scrollBehavior = "auto";
      el.scrollTop = value * maxScroll;
      scrollAccumRef.current = 0;
      setProgress(value);
      const rate = getScrollRate(ppf, playbackFps) * effectiveScale;
      setCurrentSec((value * maxScroll) / rate);
      setDurationSec(maxScroll / rate);
    },
    [previewRef, ppf, playbackFps, effectiveScale],
  );

  const handleSeekEnd = useCallback(() => {
    isSeekingRef.current = false;
    lastTsRef.current = 0;
    syncProgressFromScroll();
  }, [syncProgressFromScroll]);

  const onBarMouseEnter = useCallback(() => {
    isPlaybarHoveredRef.current = true;
    revealPlaybar();
  }, [revealPlaybar]);

  const onBarMouseLeave = useCallback(() => {
    isPlaybarHoveredRef.current = false;
    if (!isPlayingRef.current) return;
    if (!isPreviewHoveredRef.current) {
      hidePlaybar();
    } else {
      schedulePlaybarHide();
    }
  }, [hidePlaybar, schedulePlaybarHide]);

  return {
    isPlaying,
    isPlayingRef,
    progress,
    currentLabel: formatPreviewTime(currentSec),
    durationLabel: formatPreviewTime(durationSec),
    isPlaybarVisible,
    isAtLastBlock,
    syncProgressFromScroll,
    handlePlayPause,
    handleRestart,
    handleSeek,
    handleSeekStart,
    handleSeekEnd,
    onBarMouseEnter,
    onBarMouseLeave,
  };
}
